/**
 * NOS-127: Load / update podcast_settings on the current org (requires org update permission).
 */
import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { useOrgStore } from "@/stores/orgStore"
import type { Json } from "@/types/database"

export interface PodcastSettingsForm {
  title: string
  description: string
  author: string
  cover_art_url: string
  language: string
  explicit: boolean
}

function parseSettings(raw: Json | null | undefined): PodcastSettingsForm {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {
      title: "",
      description: "",
      author: "",
      cover_art_url: "",
      language: "en-us",
      explicit: false,
    }
  }
  const o = raw as Record<string, unknown>
  return {
    title: typeof o.title === "string" ? o.title : "",
    description: typeof o.description === "string" ? o.description : "",
    author: typeof o.author === "string" ? o.author : "",
    cover_art_url: typeof o.cover_art_url === "string" ? o.cover_art_url : "",
    language: typeof o.language === "string" ? o.language : "en-us",
    explicit: o.explicit === true || o.explicit === "true",
  }
}

export function useMediaSettings() {
  const { currentOrg, fetchUserOrgs } = useOrgStore()
  const orgId = currentOrg?.id ?? null
  const [settings, setSettings] = useState<PodcastSettingsForm>(parseSettings(null))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId) {
      setSettings(parseSettings(null))
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: e } = await supabase
      .from("organizations")
      .select("podcast_settings")
      .eq("id", orgId)
      .maybeSingle()
    if (e) {
      setError(e.message)
    } else {
      setSettings(parseSettings((data?.podcast_settings as Json) ?? null))
    }
    setLoading(false)
  }, [orgId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const save = useCallback(
    async (next: PodcastSettingsForm): Promise<boolean> => {
      if (!orgId) return false
      setSaving(true)
      setError(null)
      const podcast_settings: Json = {
        title: next.title.trim() || null,
        description: next.description.trim() || null,
        author: next.author.trim() || null,
        cover_art_url: next.cover_art_url.trim() || null,
        language: next.language.trim() || "en-us",
        explicit: next.explicit,
      }
      const { error: uErr } = await supabase
        .from("organizations")
        .update({
          podcast_settings,
          updated_at: new Date().toISOString(),
        })
        .eq("id", orgId)
      setSaving(false)
      if (uErr) {
        setError(uErr.message)
        return false
      }
      await fetchUserOrgs()
      setSettings(next)
      return true
    },
    [orgId, fetchUserOrgs]
  )

  return { settings, loading, saving, error, refresh, save }
}
